import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import { Parent, ParentNote, Contact, ClientDocument, ClientFile } from "@visitproof/types";
import { ParentService } from "../services/ParentService";
import { useAccount } from "./AccountContext";
import { useAuth } from "./AuthContext";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import {
  RelationshipToChild,
  CustodyStatus,
} from "@visitproof/types";

// Re-type for backward compatibility
type File = ClientFile;
type Document = ClientDocument;

interface Errors {
  list?: Error | null;
  details?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface ParentContextProps {
  loading: Loadings;
  error: Errors;
  files: File[];
  notes: ParentNote[];
  parents: Parent[];
  contacts: Contact[];
  documents: Document[];
  selectedParent: Parent | null;
  getParentById: (id: string) => Parent | undefined;
  setSelectedParent: (parentId: string) => void;
  createParent: (
    parentData: Omit<Parent, "id" | "createdAt" | "updatedAt" | "accountId">,
  ) => Promise<string>;
  updateParent: (
    parentId: string,
    updatedData: Partial<Omit<Parent, "id" | "createdAt" | "updatedAt">>,
  ) => void;
  addFileToParent: (
    parentId: string,
    fileData: Omit<File, "id" | "createdAt" | "updatedAt" | "createdBy">,
  ) => Promise<string>;
  updateFileInParent: (
    parentId: string,
    fileId: string,
    updatedData: Partial<
      Omit<File, "id" | "createdAt" | "updatedAt" | "createdBy">
    >,
  ) => void;
  deleteFileFromParent: (parentId: string, fileId: string) => Promise<void>;
  deleteParentById: (parentId: string) => void;
  deleteContactFromParent: (parentId: string, contactId: string) => void;
  // Notes management
  addNoteToParent: (parentId: string, content: string) => Promise<string>;
  updateParentNote: (
    parentId: string,
    noteId: string,
    content: string,
  ) => Promise<void>;
  deleteNoteFromParent: (parentId: string, noteId: string) => Promise<void>;
  refreshNotes: (parentId: string) => Promise<void>;
}

const ParentContext = createContext<ParentContextProps | undefined>(undefined);

export const useParent = (): ParentContextProps => {
  const context = useContext(ParentContext);
  if (!context) {
    throw new Error("useParent must be used within a ParentProvider");
  }
  return context;
};

export const ParentProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user, claims } = useAuth();
  const { account } = useAccount();
  const [files, setFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState<ParentNote[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [parents, setParents] = useState<Parent[]>([]);
  const [selectedParent, setSelectedParent] = useState<Parent | null>(null);
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const [loading, setLoading] = useState<Loadings>({
    list: true,
    details: true,
  });
  const [error, setError] = useState<Errors>({});

  // Track if we're currently fetching to prevent duplicate calls
  const isFetchingRef = useRef(false);

  // Use accountId from claims (works for all user types) or fall back to account.id
  const accountId = claims?.accountId || account?.id;

  // Fetch parents list
  useEffect(() => {
    if (!user || !accountId) {
      setLoading((prev) => ({ ...prev, list: false }));
      return;
    }
    setError((prev) => ({ ...prev, list: null }));
    setLoading((prev) => ({ ...prev, list: true }));
    const docRef = query(
      collection(db, "parents"),
      where("accountId", "==", accountId),
      orderBy("lastName", "asc"),
      orderBy("firstName", "asc"),
    );
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        const parentsData: Parent[] = [];
        docSnapshot.forEach((doc) => {
          parentsData.push({ id: doc.id, ...doc.data() } as Parent);
        });
        setParents(parentsData);
        setLoading((prev) => ({ ...prev, list: false }));
      },
      (err) => {
        console.error("Error fetching parents: ", err);
        setError((prev) => ({ ...prev, list: err }));
        setLoading((prev) => ({ ...prev, list: false }));
      },
    );
    return () => {
      unsubscribe();
    };
  }, [accountId, user]);

  // Fetch selected parent details when ID changes
  useEffect(() => {
    if (!selectedParentId || !accountId) return;

    // Prevent duplicate fetches
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    setError((prev) => ({ ...prev, details: null }));
    setLoading((prev) => ({ ...prev, details: true }));

    const fetchParentData = async () => {
      try {
        const [parentData, filesData, notesData, contactsData, documentsData] = await Promise.all([
          ParentService.getParent(selectedParentId),
          ParentService.getParentFiles(selectedParentId),
          ParentService.getParentNotes(selectedParentId),
          ParentService.getParentContacts(selectedParentId, accountId),
          ParentService.getParentDocuments(selectedParentId, accountId),
        ]);

        setSelectedParent(parentData);
        setFiles(filesData);
        setNotes(notesData);
        setContacts(contactsData);
        setDocuments(documentsData);
        setLoading((prev) => ({ ...prev, details: false }));
      } catch (err) {
        console.error("Error fetching parent details:", err);
        setError((prev) => ({ ...prev, details: err as Error }));
        setLoading((prev) => ({ ...prev, details: false }));
      } finally {
        isFetchingRef.current = false;
      }
    };

    fetchParentData();
  }, [selectedParentId, accountId]);

  const setSelectedParentById = useCallback((parentId: string) => {
    // Only update if the ID is different to prevent loops
    setSelectedParentId((prevId) => {
      if (prevId === parentId) {
        return prevId;
      }
      return parentId;
    });
  }, []);

  const createParent = async (
    parentData: Omit<Parent, "id" | "createdAt" | "updatedAt" | "accountId">,
  ): Promise<string> => {
    if (!accountId) {
      throw new Error("Account not available");
    }

    // Call the Firebase callable function to create the parent
    // This properly handles user lookup/creation, claims, and membership
    const createParentFn = httpsCallable<
      {
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        caseId: string;
        dob?: string;
        relationshipToChild: RelationshipToChild | string;
        custodyStatus: CustodyStatus | string;
        mentalHealthConcerns?: boolean;
        isCurrentlyReceivingTherapy?: boolean;
        historyOfSubstanceAbuse?: boolean;
        notes?: string;
        avatarUrl?: string;
      },
      {
        success: boolean;
        parentId: string;
        userId: string;
        isNewUser: boolean;
      }
    >(functions, "createParent");

    // Convert dob from Timestamp to ISO string if present
    let dobString: string | undefined;
    if (parentData.dob) {
      if (parentData.dob instanceof Timestamp) {
        dobString = parentData.dob.toDate().toISOString();
      } else if (typeof parentData.dob === "object" && "toDate" in parentData.dob) {
        dobString = (parentData.dob as Timestamp).toDate().toISOString();
      } else if (typeof parentData.dob === "string") {
        dobString = parentData.dob;
      }
    }

    const result = await createParentFn({
      firstName: parentData.firstName,
      lastName: parentData.lastName,
      email: parentData.email || "",
      phoneNumber: parentData.phoneNumber || "",
      caseId: parentData.caseId,
      dob: dobString,
      relationshipToChild: parentData.relationshipToChild,
      custodyStatus: parentData.custodyStatus,
      mentalHealthConcerns: parentData.mentalHealthConcerns,
      isCurrentlyReceivingTherapy: parentData.isCurrentlyReceivingTherapy,
      historyOfSubstanceAbuse: parentData.historyOfSubstanceAbuse,
      notes: parentData.notes,
      avatarUrl: parentData.avatarUrl,
    });

    return result.data.parentId;
  };

  // Helper to refresh the currently selected parent's data
  const refreshSelectedParent = useCallback(async (parentId: string) => {
    try {
      const [parentData, filesData, notesData, contactsData, documentsData] = await Promise.all([
        ParentService.getParent(parentId),
        ParentService.getParentFiles(parentId),
        ParentService.getParentNotes(parentId),
        accountId ? ParentService.getParentContacts(parentId, accountId) : Promise.resolve([]),
        accountId ? ParentService.getParentDocuments(parentId, accountId) : Promise.resolve([]),
      ]);

      setSelectedParent(parentData);
      setFiles(filesData);
      setNotes(notesData);
      setContacts(contactsData);
      setDocuments(documentsData);
    } catch (err) {
      console.error("Error refreshing parent details:", err);
    }
  }, [accountId]);

  // Helper to refresh just notes
  const refreshNotes = useCallback(async (parentId: string) => {
    try {
      const notesData = await ParentService.getParentNotes(parentId);
      setNotes(notesData);
    } catch (err) {
      console.error("Error refreshing parent notes:", err);
    }
  }, []);

  const updateParent = async (
    parentId: string,
    updatedData: Partial<Omit<Parent, "id" | "createdAt" | "updatedAt">>,
  ) => {
    await ParentService.updateParent(parentId, updatedData);
    await refreshSelectedParent(parentId);
  };

  const deleteParentById = async (parentId: string) => {
    await ParentService.deleteParent(parentId);
  };

  const addFileToParent = async (
    parentId: string,
    fileData: Omit<File, "id" | "createdAt" | "updatedAt" | "createdBy">,
  ): Promise<string> => {
    const newFileId = await ParentService.addFileToParent(parentId, {
      ...fileData,
      createdBy: user!.uid,
    });
    await refreshSelectedParent(parentId);
    return newFileId;
  };

  const updateFileInParent = async (
    parentId: string,
    fileId: string,
    updatedData: Partial<
      Omit<File, "id" | "createdAt" | "updatedAt" | "createdBy">
    >,
  ) => {
    await ParentService.updateFileInParent(parentId, fileId, updatedData);
    await refreshSelectedParent(parentId);
  };

  const deleteFileFromParent = async (
    parentId: string,
    fileId: string,
  ): Promise<void> => {
    await ParentService.deleteFileFromParent(parentId, fileId);
    await refreshSelectedParent(parentId);
  };

  const deleteContactFromParent = async (
    parentId: string,
    contactId: string,
  ): Promise<void> => {
    await ParentService.deleteContactFromParent(parentId, contactId);
    await refreshSelectedParent(parentId);
  };

  const addNoteToParent = async (
    parentId: string,
    content: string,
  ): Promise<string> => {
    if (!accountId || !user?.uid) {
      throw new Error("Account or user not available");
    }
    const noteId = await ParentService.addNoteToParent(parentId, {
      content,
      createdByUserId: user.uid,
      createdByName: user.displayName || user.email || "Unknown",
      accountId,
      parentId,
    });
    await refreshNotes(parentId);
    return noteId;
  };

  const updateParentNote = async (
    parentId: string,
    noteId: string,
    content: string,
  ): Promise<void> => {
    await ParentService.updateParentNote(parentId, noteId, content);
    await refreshNotes(parentId);
  };

  const deleteNoteFromParent = async (
    parentId: string,
    noteId: string,
  ): Promise<void> => {
    await ParentService.deleteNoteFromParent(parentId, noteId);
    await refreshNotes(parentId);
  };

  const getParentById = (id: string): Parent | undefined => {
    return parents.find((p) => p.id === id);
  };

  return (
    <ParentContext.Provider
      value={{
        loading,
        error,
        contacts,
        documents,
        files,
        notes,
        parents,
        selectedParent,
        getParentById,
        createParent,
        updateParent,
        deleteParentById,
        setSelectedParent: setSelectedParentById,
        addFileToParent,
        deleteFileFromParent,
        deleteContactFromParent,
        updateFileInParent,
        addNoteToParent,
        updateParentNote,
        deleteNoteFromParent,
        refreshNotes,
      }}
    >
      {children}
    </ParentContext.Provider>
  );
};
